import {
    Avatar,
    classNames,
    layout,
    PushButton,
    typography,
    useAsync,
    usePlatform,
    View,
    ViewContent,
    ViewFooter,
    ViewFooterItem
} from "@variocube/cube-ui";
import * as React from "react";
import {Fragment, useCallback, useMemo} from "react";
import {stylesheet} from "typestyle";
import {Delivery, DeliveryState, formatDateTime, getOccupancyDeliveries, PickupEvent} from "./types";
import {findDeliveryOccupancies} from "./utils";
import {useLocalization} from "../i18n";
import {DeliveryStateDisplay} from "./DeliveryStateDisplay";
import {DeliveryTagsDisplay} from "./DeliveryTagsDisplay";

interface DeliveryDialogProps {
    delivery: Delivery;
    onClose: () => void;
    onStore: (delivery: Delivery) => void;
    onPickUp: (pickupEvent: PickupEvent) => void;
    onAddLocker: () => void;
}

export function DeliveryView({delivery, onClose, onPickUp, onStore, onAddLocker}: DeliveryDialogProps) {
    const platform = usePlatform();
    const {t, language} = useLocalization();

    const {result: occupancies = []} = useAsync(async () => {
        return findDeliveryOccupancies(await platform.getOccupancies(), delivery);
    }, [platform, delivery]);

    const id = (delivery.metadata && delivery.metadata.foreignId) || delivery.delivery.deliveryId;

    const canStore = useMemo<boolean>(() =>
            ((delivery.delivery.state == DeliveryState.Created) && occupancies.length == 0) ||
            (delivery.delivery.state == DeliveryState.Reserved)
        , [delivery, occupancies]);

    const canPickup = useMemo<boolean>(() => {
        if(occupancies.length == 0) {
            return false;
        }
        for(let occupancy of occupancies) {
            const deliveries = getOccupancyDeliveries(occupancy);
            if (deliveries && deliveries.length > 0) {
                for (let occupancyDelivery of deliveries) {
                    if (!occupancyDelivery.delivery.deliveryId) {
                        return false;
                    }
                }
            }
        }
        return true;
    }, [occupancies]);

    const handleStoreClick = useCallback(() => onStore(delivery), [onStore, delivery]);

    const handlePickUpClick = useCallback(() => onPickUp({occupancies, pickupRecipient: undefined}), [onPickUp, occupancies]);

    return (
        <View>
            <ViewContent className={classNames(layout.horizontal, layout.spaceBetween, layout.paddingLarge)}>
                <div className={typography.bigger}>
                    <h5 className={styles.heading}>{t("Delivery")}</h5>
                    {delivery.order &&
                        <Fragment>
                            {delivery.order.description &&
                                <div>{delivery.order.description}</div>
                            }
                            {delivery.order.orderId &&
                                <div>{t("OrderId")}: {delivery.order.orderId}</div>
                            }
                        </Fragment>
                    }
                    <div>{t("DeliveryId")}: {id}</div>

                    <h5 className={styles.heading}>{t("Recipient")}</h5>
                    {delivery.recipients.map(recipient => (
                        <div>{recipient.recipientName}</div>
                    ))}

                    <h5 className={styles.heading}>{t("Storage")}</h5>
                    {delivery.storage.storageTimeMax && (
                        <div>{t("StorageMaxTime")}: {delivery.storage.storageTimeMax}</div>
                    )}
                    {delivery.storage.hazardousGoods && (
                        <div>{t("HazardousGoods")}: {delivery.storage.hazardousGoods}</div>
                    )}
                    {delivery.delivery?.storedAt && (
                        <div>{t("StoredAt")}: {formatDateTime(delivery.delivery?.storedAt, language)}</div>
                    )}
                </div>

                <div>
                    <div>
                        <div className={styles.heading}>{t("Status")}</div>
                        <DeliveryStateDisplay deliveryState={delivery.delivery.state} />
                    </div>

                    {(delivery.tags && delivery.tags.length > 0) &&
                        <div>
                            <div className={styles.heading}>{t("Tags")}</div>
                            <DeliveryTagsDisplay tags={delivery.tags} />
                        </div>
                    }

                    {occupancies.length > 0 && (
                        <div>
                            <div className={styles.heading}>{t("Box")}</div>
                            {occupancies.map(occupancy => (
                                <Avatar size={2} key={occupancy.uuid}>{occupancy.boxNumber}</Avatar>
                            ))}
                        </div>
                    )}

                </div>
            </ViewContent>
            <ViewFooter className={classNames(layout.horizontal, layout.spaceBetween, layout.paddingLarge)}>
                <ViewFooterItem>
                    <PushButton onClick={onClose}>
                        {t("Close")}
                    </PushButton>
                </ViewFooterItem>
                {canStore && (
                    <ViewFooterItem>
                        <PushButton primary onClick={handleStoreClick}>
                            {t("Store")}
                        </PushButton>
                    </ViewFooterItem>
                )}
                {occupancies.length > 0 && (
                    <ViewFooterItem>
                        <PushButton onClick={onAddLocker}>
                            {t("DeliveryAddLocker")}
                        </PushButton>
                    </ViewFooterItem>
                )}
                {occupancies.length > 0 && (
                    <ViewFooterItem>
                        <PushButton primary onClick={handlePickUpClick} disabled={!canPickup}>
                            {t("Pickup")}
                        </PushButton>
                    </ViewFooterItem>
                )}
            </ViewFooter>
        </View>
    );
}


const styles = stylesheet({
    heading: {
        fontSize: "1.25em",
        fontWeight: 500,
        margin: "0.66em 0"
    }
});